/**
 * RoTooltip - 文字提示 Web 组件
 *
 * 功能特性：
 * - 支持 12 种弹出位置：top/bottom/left/right 及其 start/end 变体
 * - 自动翻转：当空间不足时自动切换到对侧
 * - Portal 支持：下拉面板传送到 body，避免被父容器裁切
 * - 触发方式：支持 hover、focus 和 manual 手动模式
 * - 最大宽度：PC 端 400px，移动端 342px，支持视口保护
 * - 最大高度：默认 37vh，内容超出后显示纵向滚动条
 * - 主题色：支持 background-color / text-color 属性，或 --ro-tooltip-bg / --ro-tooltip-color 变量
 * - 触摸端：点击触发区切换开关；页面滚动或点击外部区域时自动关闭（气泡内部滚动不关闭）
 *
 * 参数说明：
 * - content: 提示文本内容（纯文本；与 slot="content" 同时存在时，插槽优先）
 * - slot="content": 自定义 tooltip 内容（支持 HTML / 富样式，打开时移入 portal，关闭后还原）
 * - placement: 出现位置 (top/top-start/top-end/bottom/bottom-start/bottom-end/left/left-start/left-end/right/right-start/right-end)
 * - disabled: 是否禁用
 * - visible-arrow: 是否显示小箭头 (默认 true)
 * - manual: 是否手动控制 (默认 false)
 * - enterable: 鼠标是否可进入 tooltip (默认 true)
 * - hide-after: 延迟关闭时间 (ms)
 * - tabindex: 触发元素的 tabindex (默认 0)
 * - background-color: 气泡背景色 (默认 rgba(0, 0, 0, 0.9))
 * - text-color: 文字颜色 (默认 #ffffff)
 */
class RoTooltip extends HTMLElement {
  static get DEFAULT_BG() {
    return "rgba(0, 0, 0, 0.9)";
  }

  static get DEFAULT_COLOR() {
    return "#ffffff";
  }

  /** 触摸/粗指针设备（无 hover 或 coarse pointer） */
  static get TOUCH_MEDIA_QUERY() {
    if (!this._touchMediaQuery) {
      this._touchMediaQuery = window.matchMedia("(hover: none), (pointer: coarse)");
    }
    return this._touchMediaQuery;
  }

  /** Portal 在 body 下无法继承 host 变量，需同步到气泡节点的布局/滚动条变量 */
  static get LAYOUT_CSS_VARS() {
    return [
      "--ro-tooltip-max-width",
      "--ro-tooltip-max-height",
      "--ro-tooltip-scrollbar-thumb",
      "--ro-tooltip-scrollbar-thumb-hover",
    ];
  }

  static get PORTAL_STYLES() {
    return `
      .ro-tooltip-portal {
        position: fixed;
        left: 0;
        top: 0;
        width: 0;
        height: 0;
        z-index: 10000;
        pointer-events: none;
      }
      .ro-tooltip-content {
        position: fixed;
        background: var(--ro-tooltip-bg, rgba(0, 0, 0, 0.9));
        color: var(--ro-tooltip-color, #ffffff);
        padding: 12px 16px;
        border-radius: 4px;
        font-size: 12px;
        line-height: 16px;
        font-family: "Proxima Nova", sans-serif;
        box-sizing: border-box;
        z-index: 10001;
        pointer-events: auto;
        word-wrap: break-word;
        white-space: pre-line;
        max-width: min(var(--ro-tooltip-max-width, 400px), calc(100vw - 32px));
        max-height: var(--ro-tooltip-max-height, 37vh);
        overflow-y: auto;
        overscroll-behavior: contain;
        scrollbar-width: thin;
        scrollbar-color: var(--ro-tooltip-scrollbar-thumb, rgba(255, 255, 255, 0.45)) transparent;
        transition: opacity 0.2s ease;
        opacity: 0;
      }
      .ro-tooltip-content.is-rich-content {
        white-space: normal;
      }
      .ro-tooltip-content::-webkit-scrollbar {
        width: 4px;
      }
      .ro-tooltip-content::-webkit-scrollbar-button {
        display: none;
      }
      .ro-tooltip-content::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 2px;
      }
      .ro-tooltip-content::-webkit-scrollbar-thumb {
        background: var(--ro-tooltip-scrollbar-thumb, rgba(255, 255, 255, 0.45));
        border-radius: 2px;
      }
      .ro-tooltip-content::-webkit-scrollbar-thumb:hover {
        background: var(--ro-tooltip-scrollbar-thumb-hover, rgba(255, 255, 255, 0.65));
      }
      .ro-tooltip-content.is-visible,
      .ro-tooltip-arrow.is-visible {
        opacity: 1;
      }
      .ro-tooltip-arrow {
        position: absolute;
        width: 0;
        height: 0;
        border-style: solid;
        z-index: 10002;
        opacity: 0;
        transition: opacity 0.2s ease;
      }
      @media (max-width: 1183px) {
        .ro-tooltip-content {
          max-width: min(var(--ro-tooltip-max-width, 342px), calc(100vw - 32px));
        }
      }
    `;
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._isOpen = false;
    this._timer = null;
    /** 淡出结束后从 body 摘除 portal 的定时器（必须与 _timer 分开记录，否则会与快速反复悬停的竞态叠在一起） */
    this._removeTimer = null;
    this._portalRoot = null;
    this._tooltipEl = null;
    this._arrowEl = null;
    /** content 插槽节点暂存（打开时移入 portal，关闭后还原到 host） */
    this._slotContentNodes = [];
    /** 当前实例解析后的背景色，供箭头 border 着色 */
    this._resolvedBackground = RoTooltip.DEFAULT_BG;
    this._boundUpdatePosition = this._updatePosition.bind(this);
    this._boundHandleScroll = this._handleScroll.bind(this);
    this._boundHandleOutsidePointer = this._handleOutsidePointer.bind(this);
    this._boundHandleMouseEnter = this._handleMouseEnter.bind(this);
    this._boundHandleMouseLeave = this._handleMouseLeave.bind(this);
    this._boundHandleFocus = this._handleFocus.bind(this);
    this._boundHandleBlur = this._handleBlur.bind(this);
    /** 触摸端：用 click 切换开关，避免 hover/focus 粘滞导致同一触发器无法再次打开 */
    this._boundHandleTouchToggle = this._handleTouchToggle.bind(this);
    /** 阻止点击/触摸冒泡到外层 <a> 等可点击容器，避免误跳转 */
    this._boundStopActivate = this._stopActivate.bind(this);
    this._boundHandleContentSlotChange = this._handleContentSlotChange.bind(this);
    this._render();
  }

  /**
   * 触摸端点击 tooltip 时，阻止合成 click 冒泡到父级链接。
   */
  _stopActivate(event) {
    event.stopPropagation();
    if (event.type === "click" && this.closest("a")) {
      event.preventDefault();
    }
  }

  _render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-flex;
        }
        .ro-tooltip-trigger {
          display: inline-flex;
          align-items: center;
          outline: none;
        }
      </style>
      <div hidden aria-hidden="true">
        <slot name="content"></slot>
      </div>
      <div class="ro-tooltip-trigger" tabindex="${this.getAttribute("tabindex") || "0"}">
        <slot></slot>
      </div>
    `;
    this._triggerEl = this.shadowRoot.querySelector(".ro-tooltip-trigger");
    this._contentSlot = this.shadowRoot.querySelector('slot[name="content"]');
  }

  connectedCallback() {
    this._contentSlot.addEventListener("slotchange", this._boundHandleContentSlotChange);
    if (!this.hasAttribute("manual")) {
      this._bindTriggerInteractionListeners();
    }
    this.addEventListener("click", this._boundStopActivate);
    this.addEventListener("touchend", this._boundStopActivate);
  }

  disconnectedCallback() {
    this._contentSlot.removeEventListener("slotchange", this._boundHandleContentSlotChange);
    this._unbindTriggerInteractionListeners();
    this.removeEventListener("click", this._boundStopActivate);
    this.removeEventListener("touchend", this._boundStopActivate);
    this._hide(true);
  }

  /** 按设备能力绑定触发区交互：触摸端 click 切换，桌面端 hover + focus */
  _bindTriggerInteractionListeners() {
    if (this._isTouchLike()) {
      this._triggerEl.addEventListener("click", this._boundHandleTouchToggle);
      return;
    }
    this._triggerEl.addEventListener("mouseenter", this._boundHandleMouseEnter);
    this._triggerEl.addEventListener("mouseleave", this._boundHandleMouseLeave);
    this._triggerEl.addEventListener("focusin", this._boundHandleFocus);
    this._triggerEl.addEventListener("focusout", this._boundHandleBlur);
  }

  _unbindTriggerInteractionListeners() {
    this._triggerEl.removeEventListener("click", this._boundHandleTouchToggle);
    this._triggerEl.removeEventListener("mouseenter", this._boundHandleMouseEnter);
    this._triggerEl.removeEventListener("mouseleave", this._boundHandleMouseLeave);
    this._triggerEl.removeEventListener("focusin", this._boundHandleFocus);
    this._triggerEl.removeEventListener("focusout", this._boundHandleBlur);
  }

  static get observedAttributes() {
    return ["content", "placement", "tabindex", "background-color", "text-color"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === "tabindex") {
      this._triggerEl.setAttribute("tabindex", newValue || "0");
      return;
    }
    if (name === "background-color" || name === "text-color") {
      this._applyTooltipTheme();
      if (this._isOpen) {
        this._updatePosition();
      }
      return;
    }
    if (!this._isOpen || !this._tooltipEl) return;
    if (name === "content" && !this._usesSlotContentMode()) {
      this._applyTextContent();
      this._updatePosition();
    } else if (name === "placement") {
      this._updatePosition();
    }
  }

  /** 获取 content 插槽的有效节点 */
  _getAssignedContentNodes() {
    return this._contentSlot.assignedNodes({ flatten: true }).filter((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) return true;
      if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim().length > 0;
      return false;
    });
  }

  /** 是否处于插槽内容模式（含已移入 portal 的节点） */
  _usesSlotContentMode() {
    return this._slotContentNodes.length > 0 || this._getAssignedContentNodes().length > 0;
  }

  /** 插槽内容变更：打开状态下刷新 portal 内容并重算位置 */
  _handleContentSlotChange() {
    if (!this._isOpen || !this._tooltipEl || this._slotContentNodes.length > 0) return;
    this._applyTooltipContent();
    this._updatePosition();
  }

  /** 将 content 插槽节点移入 portal 气泡（保留 live DOM，便于 data-bind 等动态更新） */
  _moveSlotContentToTooltip() {
    const nodes = this._getAssignedContentNodes();
    if (!nodes.length) return;

    this._slotContentNodes = nodes;
    this._tooltipEl.classList.add("is-rich-content");
    this._tooltipEl.replaceChildren(...nodes);
  }

  /** 关闭 portal 前把插槽节点还原到 host，以便下次 slot 分配 */
  _restoreSlotContent() {
    if (!this._slotContentNodes.length) return;
    this._slotContentNodes.forEach((node) => {
      if (node.parentNode === this._tooltipEl) {
        this.appendChild(node);
      }
    });
    this._slotContentNodes = [];
  }

  /** 使用 content 属性渲染纯文本 */
  _applyTextContent() {
    this._tooltipEl.classList.remove("is-rich-content");
    this._tooltipEl.textContent = this.getAttribute("content") || "";
  }

  /** 插槽优先，否则回退到 content 属性 */
  _applyTooltipContent() {
    if (this._usesSlotContentMode()) {
      if (!this._slotContentNodes.length) {
        this._moveSlotContentToTooltip();
      }
      return;
    }
    this._restoreSlotContent();
    this._applyTextContent();
  }

  _handleMouseEnter() {
    if (this._isTouchLike() || this.hasAttribute("disabled")) return;
    this._show();
  }

  _handleMouseLeave(event) {
    if (this._isTouchLike() || this.hasAttribute("disabled")) return;
    if (this._isEnterable() && this._isHoverTargetInsideTooltip(event.relatedTarget)) {
      return;
    }
    this._hide();
  }

  _handleFocus() {
    if (this._isTouchLike() || this.hasAttribute("disabled")) return;
    this._show();
  }

  _handleBlur(event) {
    if (this._isTouchLike() || this.hasAttribute("disabled")) return;
    // 焦点在 trigger 与插槽内可聚焦子节点之间移动时不关闭
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    this._hide();
  }

  /** 触摸端：再次点击同一触发器可关闭；不依赖 hover/focus 重复触发 */
  _handleTouchToggle(event) {
    if (this.hasAttribute("disabled")) return;
    event.preventDefault();
    event.stopPropagation();
    if (this._isOpen) {
      this._hide(true);
    } else {
      this._show();
    }
  }

  _show() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    if (this._removeTimer) {
      clearTimeout(this._removeTimer);
      this._removeTimer = null;
    }

    if (this._isOpen) return;

    // 上一轮已执行 performHide、但「200ms 后移除 DOM」尚未跑完时再次打开：会先 new 一个新 portal，
    // 旧的 portal 失去引用却仍留在 body，表现为只有小三角箭头残留在页面上。
    if (this._portalRoot) {
      this._removeTooltip();
    }

    this._isOpen = true;
    this._createTooltip();
    this._updatePosition();

    requestAnimationFrame(() => {
      this._tooltipEl?.classList.add("is-visible");
      this._arrowEl?.classList.add("is-visible");
    });

    this._attachGlobalListeners();
  }

  _hide(immediate = false) {
    if (!this._isOpen) return;

    const hideDelay = parseInt(this.getAttribute("hide-after"), 10) || 0;

    const performHide = () => {
      this._timer = null;
      this._isOpen = false;
      if (this._tooltipEl) {
        this._tooltipEl.classList.remove("is-visible");
        this._arrowEl?.classList.remove("is-visible");
        if (this._removeTimer) {
          clearTimeout(this._removeTimer);
          this._removeTimer = null;
        }
        // 等待淡出 transition 后再摘 DOM，必须用同一字段记录，便于 _show 时取消并避免双 portal
        this._removeTimer = setTimeout(() => {
          this._removeTimer = null;
          this._removeTooltip();
        }, 200);
      } else {
        this._removeTooltip();
      }
      this._detachGlobalListeners();
      if (this._isTouchLike()) {
        this._releaseTouchFocus();
      }
    };

    if (immediate) {
      performHide();
    } else {
      this._timer = setTimeout(performHide, hideDelay || 100);
    }
  }

  _createTooltip() {
    this._injectStyles();

    this._portalRoot = document.createElement("div");
    this._portalRoot.className = "ro-tooltip-portal";

    this._tooltipEl = document.createElement("div");
    this._tooltipEl.className = "ro-tooltip-content";
    this._applyTooltipContent();
    this._applyTooltipTheme();

    if (this.getAttribute("visible-arrow") !== "false") {
      this._arrowEl = document.createElement("div");
      this._arrowEl.className = "ro-tooltip-arrow";
      this._portalRoot.appendChild(this._arrowEl);
    }

    if (this._isEnterable()) {
      this._bindEnterableHover();
    }

    this._portalRoot.appendChild(this._tooltipEl);
    document.body.appendChild(this._portalRoot);
  }

  _removeTooltip() {
    if (this._removeTimer) {
      clearTimeout(this._removeTimer);
      this._removeTimer = null;
    }
    this._restoreSlotContent();
    this._portalRoot?.remove();
    this._portalRoot = null;
    this._tooltipEl = null;
    this._arrowEl = null;
  }

  _injectStyles() {
    if (document.getElementById("ro-tooltip-portal-styles")) return;
    const style = document.createElement("style");
    style.id = "ro-tooltip-portal-styles";
    style.textContent = RoTooltip.PORTAL_STYLES;
    document.head.appendChild(style);
  }

  /** 是否为触摸/粗指针环境 */
  _isTouchLike() {
    return RoTooltip.TOUCH_MEDIA_QUERY.matches;
  }

  /** 关闭后释放 trigger / 插槽内 button 的焦点，避免下次点击无法再次 toggle */
  _releaseTouchFocus() {
    const active = document.activeElement;
    if (active instanceof HTMLElement && this.contains(active)) {
      active.blur();
    }
    this._triggerEl?.blur();
  }

  /** scroll 是否发生在 tooltip 内容区（内部列表滚动时不应关闭） */
  _isScrollInsideTooltip(target) {
    if (!target || !this._tooltipEl) return false;
    return target === this._tooltipEl || this._tooltipEl.contains(target);
  }

  /** 事件是否落在 trigger 或 portal 区域内 */
  _isEventInsideTooltipArea(event) {
    const path = typeof event.composedPath === "function" ? event.composedPath() : [];
    return path.includes(this) || (this._portalRoot && path.includes(this._portalRoot));
  }

  _attachGlobalListeners() {
    window.addEventListener("scroll", this._boundHandleScroll, true);
    window.addEventListener("resize", this._boundUpdatePosition);
    if (this._isTouchLike() && !this.hasAttribute("manual")) {
      document.addEventListener("touchstart", this._boundHandleOutsidePointer, true);
      document.addEventListener("click", this._boundHandleOutsidePointer, true);
    }
  }

  _detachGlobalListeners() {
    window.removeEventListener("scroll", this._boundHandleScroll, true);
    window.removeEventListener("resize", this._boundUpdatePosition);
    document.removeEventListener("touchstart", this._boundHandleOutsidePointer, true);
    document.removeEventListener("click", this._boundHandleOutsidePointer, true);
  }

  /** 桌面端 scroll 仅重定位；触摸端页面滚动则关闭 */
  _handleScroll(event) {
    if (this._isTouchLike() && !this.hasAttribute("manual")) {
      if (this._isScrollInsideTooltip(event.target)) return;
      this._hide(true);
      return;
    }
    this._updatePosition();
  }

  /** 触摸端点击 trigger / tooltip 以外区域时关闭 */
  _handleOutsidePointer(event) {
    if (!this._isOpen || !this._isTouchLike() || this.hasAttribute("manual")) return;
    if (this._isEventInsideTooltipArea(event)) return;
    this._hide(true);
  }

  /** enterable 默认 true，仅显式 enterable="false" 时禁用 */
  _isEnterable() {
    return this.getAttribute("enterable") !== "false";
  }

  /** 判断鼠标是否移入 tooltip 气泡或箭头区域 */
  _isHoverTargetInsideTooltip(target) {
    if (!target) return false;
    if (this._tooltipEl && (target === this._tooltipEl || this._tooltipEl.contains(target))) {
      return true;
    }
    return this._arrowEl && target === this._arrowEl;
  }

  /** 绑定气泡/箭头的 hover，便于移入后滚动或选中文案 */
  _bindEnterableHover() {
    const cancelScheduledHide = () => {
      if (this._timer) {
        clearTimeout(this._timer);
        this._timer = null;
      }
    };
    const scheduleHide = () => {
      this._hide();
    };

    this._tooltipEl.addEventListener("mouseenter", cancelScheduledHide);
    this._tooltipEl.addEventListener("mouseleave", scheduleHide);

    if (this._arrowEl) {
      this._arrowEl.style.pointerEvents = "auto";
      this._arrowEl.addEventListener("mouseenter", cancelScheduledHide);
      this._arrowEl.addEventListener("mouseleave", scheduleHide);
    }
  }

  /**
   * Portal 在 body 下无法继承 host 变量，创建/更新时同步到气泡节点。
   * 优先级：HTML 属性 > host CSS 变量 > 组件默认值。
   */
  _applyTooltipTheme() {
    const hostStyle = getComputedStyle(this);
    const background =
      this.getAttribute("background-color") ||
      hostStyle.getPropertyValue("--ro-tooltip-bg").trim() ||
      RoTooltip.DEFAULT_BG;
    const color =
      this.getAttribute("text-color") ||
      hostStyle.getPropertyValue("--ro-tooltip-color").trim() ||
      RoTooltip.DEFAULT_COLOR;

    this._resolvedBackground = background;

    if (!this._tooltipEl) return;

    RoTooltip.LAYOUT_CSS_VARS.forEach((varName) => {
      const value = hostStyle.getPropertyValue(varName).trim();
      if (value) {
        this._tooltipEl.style.setProperty(varName, value);
      }
    });

    this._tooltipEl.style.setProperty("--ro-tooltip-bg", background);
    this._tooltipEl.style.setProperty("--ro-tooltip-color", color);
  }

  _updatePosition() {
    if (!this._tooltipEl || !this._triggerEl) return;

    const triggerRect = this._triggerEl.getBoundingClientRect();
    // 使用 offsetHeight/Width 避免受 transform 影响
    const tooltipHeight = this._tooltipEl.offsetHeight;
    const tooltipWidth = this._tooltipEl.offsetWidth;
    const placement = this.getAttribute("placement") || "top";
    const arrowSize = 6;
    const offset = 9; // 6px 箭头 + 3px 留白

    let top = 0;
    let left = 0;
    let actualPlacement = placement;

    // 自动翻转逻辑
    const checkSpace = (p) => {
      const space = {
        top: triggerRect.top,
        bottom: window.innerHeight - triggerRect.bottom,
        left: triggerRect.left,
        right: window.innerWidth - triggerRect.right,
      };

      if (p.startsWith("top") && space.top < tooltipHeight + offset) return "bottom";
      if (p.startsWith("bottom") && space.bottom < tooltipHeight + offset) return "top";
      if (p.startsWith("left") && space.left < tooltipWidth + offset) return "right";
      if (p.startsWith("right") && space.right < tooltipWidth + offset) return "left";
      return p;
    };

    const basePlacement = placement.split("-")[0];
    const newBase = checkSpace(basePlacement);
    if (newBase !== basePlacement) {
      actualPlacement = placement.replace(basePlacement, newBase);
    }

    // 计算位置
    switch (actualPlacement) {
      case "top":
        top = triggerRect.top - tooltipHeight - offset;
        left = triggerRect.left + (triggerRect.width - tooltipWidth) / 2;
        break;
      case "top-start":
        top = triggerRect.top - tooltipHeight - offset;
        left = triggerRect.left;
        break;
      case "top-end":
        top = triggerRect.top - tooltipHeight - offset;
        left = triggerRect.right - tooltipWidth;
        break;
      case "bottom":
        top = triggerRect.bottom + offset;
        left = triggerRect.left + (triggerRect.width - tooltipWidth) / 2;
        break;
      case "bottom-start":
        top = triggerRect.bottom + offset;
        left = triggerRect.left;
        break;
      case "bottom-end":
        top = triggerRect.bottom + offset;
        left = triggerRect.right - tooltipWidth;
        break;
      case "left":
        top = triggerRect.top + (triggerRect.height - tooltipHeight) / 2;
        left = triggerRect.left - tooltipWidth - offset;
        break;
      case "left-start":
        top = triggerRect.top;
        left = triggerRect.left - tooltipWidth - offset;
        break;
      case "left-end":
        top = triggerRect.bottom - tooltipHeight;
        left = triggerRect.left - tooltipWidth - offset;
        break;
      case "right":
        top = triggerRect.top + (triggerRect.height - tooltipHeight) / 2;
        left = triggerRect.right + offset;
        break;
      case "right-start":
        top = triggerRect.top;
        left = triggerRect.right + offset;
        break;
      case "right-end":
        top = triggerRect.bottom - tooltipHeight;
        left = triggerRect.right + offset;
        break;
    }

    // 视口保护
    left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
    top = Math.max(16, Math.min(top, window.innerHeight - tooltipHeight - 16));

    this._tooltipEl.style.top = `${top}px`;
    this._tooltipEl.style.left = `${left}px`;

    if (this._arrowEl) {
      this._updateArrowPosition(actualPlacement, triggerRect, top, left, arrowSize);
    }
  }

  _updateArrowPosition(placement, triggerRect, tooltipTop, tooltipLeft, arrowSize) {
    if (!this._arrowEl) return;

    let arrowTop = 0;
    let arrowLeft = 0;
    const bg = this._resolvedBackground;
    const tooltipWidth = this._tooltipEl.offsetWidth;
    const tooltipHeight = this._tooltipEl.offsetHeight;

    const basePlacement = placement.split("-")[0];
    const margin = 12; // 箭头距离边缘的最小距离

    switch (basePlacement) {
      case "top":
        arrowTop = tooltipTop + tooltipHeight;
        arrowLeft = triggerRect.left + triggerRect.width / 2 - arrowSize;
        arrowLeft = Math.max(tooltipLeft + margin, Math.min(tooltipLeft + tooltipWidth - margin - arrowSize * 2, arrowLeft));
        this._arrowEl.style.borderWidth = `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`;
        this._arrowEl.style.borderColor = `${bg} transparent transparent transparent`;
        break;
      case "bottom":
        arrowTop = tooltipTop - arrowSize;
        arrowLeft = triggerRect.left + triggerRect.width / 2 - arrowSize;
        arrowLeft = Math.max(tooltipLeft + margin, Math.min(tooltipLeft + tooltipWidth - margin - arrowSize * 2, arrowLeft));
        this._arrowEl.style.borderWidth = `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`;
        this._arrowEl.style.borderColor = `transparent transparent ${bg} transparent`;
        break;
      case "left":
        arrowTop = triggerRect.top + triggerRect.height / 2 - arrowSize;
        arrowLeft = tooltipLeft + tooltipWidth;
        arrowTop = Math.max(tooltipTop + margin, Math.min(tooltipTop + tooltipHeight - margin - arrowSize * 2, arrowTop));
        this._arrowEl.style.borderWidth = `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`;
        this._arrowEl.style.borderColor = `transparent transparent transparent ${bg}`;
        break;
      case "right":
        arrowTop = triggerRect.top + triggerRect.height / 2 - arrowSize;
        arrowLeft = tooltipLeft - arrowSize;
        arrowTop = Math.max(tooltipTop + margin, Math.min(tooltipTop + tooltipHeight - margin - arrowSize * 2, arrowTop));
        this._arrowEl.style.borderWidth = `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`;
        this._arrowEl.style.borderColor = `transparent ${bg} transparent transparent`;
        break;
    }

    this._arrowEl.style.top = `${arrowTop}px`;
    this._arrowEl.style.left = `${arrowLeft}px`;
  }

  // API
  show() {
    this._show();
  }

  hide() {
    this._hide(true);
  }
}

if (!customElements.get("ro-tooltip")) {
  customElements.define("ro-tooltip", RoTooltip);
}
